import { REFERENCE_RELATES_TO } from "../../constants/references";
import { definitionSelectors } from "../definition";

const getAllowedReferenceBetweenTypes = (typesById, referencesById, referencesAllIds, subjectTypeId, itemTypeId) => {
  if (subjectTypeId && itemTypeId) {
    const subjectType = typesById.get(subjectTypeId);
    const refs = referencesAllIds
      .filter(refId => refId !== REFERENCE_RELATES_TO && subjectType.references.includes(refId))
      .push(REFERENCE_RELATES_TO)
      .map(rId => referencesById.get(rId));
    let firstRef = refs.find(ref => ref.types.contains(itemTypeId));
    return firstRef ? firstRef.id : null;
  }
  return null;
};

const getAllowedReferenceBetweenTypesWithState = (state, subjectTypeId, itemTypeId) => {
  return getAllowedReferenceBetweenTypes(
    definitionSelectors.getTypesById(state),
    definitionSelectors.getReferencesById(state),
    definitionSelectors.getReferenceIds(state),
    subjectTypeId,
    itemTypeId
  );
};

const getAllowedReferenceBetweenItems = (state, subjectId, itemId) => {
  const subject = state.data.items.byId.get(subjectId);
  const object = state.data.items.byId.get(itemId);
  return subject && object ? getAllowedReferenceBetweenTypesWithState(state, subject.type, object.type) : null;
};

const getAllAllowedReferencesBetweenTypes = (state, subjectTypeId, itemTypeId) => {
  const subjectType = definitionSelectors.getTypesById(state).get(subjectTypeId);
  const refs = subjectType.references
    .map(rId => definitionSelectors.getReferencesById(state).get(rId))
    .filter(ref => ref.types.contains(itemTypeId));
  return refs;
};

const getAllAllowedReferencesBetweenItems = (state, subjectId, itemId) => {
  const subject = state.data.items.byId.get(subjectId);
  const object = state.data.items.byId.get(itemId);
  return getAllAllowedReferencesBetweenTypes(state, subject.type, object.type);
};

const getSubjectRelationshipsWithItems = (itemsById, relationshipsById, eventId, entityIdSet) =>
  itemsById
    .get(eventId)
    .references.map(rId => relationshipsById.get(rId))
    .filter(r => entityIdSet.has(r.object));

const getSubjectInverseRelationshipsWithItems = (itemsById, relationshipsById, eventId, entityIdSet) =>
  itemsById
    .get(eventId)
    .inverseReferences.map(rId => relationshipsById.get(rId))
    .filter(r => entityIdSet.has(r.subject));

const getRelationshipsBetweenItemsWithReferences = (relationshipsById, itemIds, referenceTypes) => {
  return relationshipsById
    .filter(rel => {
      return (
        itemIds.indexOf(rel.subject) >= 0 &&
        itemIds.indexOf(rel.object) >= 0 &&
        referenceTypes.indexOf(rel.reference) >= 0
      );
    })
    .toList();
};

// TODO: This can be made much more efficient by comparing the ID lists (preferably as sets)
// of each item rather than looping through every single relationship looking for matches
const getRelationshipsBetweenItems = (state, itemId1, itemId2) => {
  return state.data.relationships.byId
    .filter(rel => {
      return (itemId1 === rel.subject && itemId2 === rel.object) || (itemId1 === rel.object && itemId2 === rel.subject);
    })
    .toList();
};

const relationshipMatches = (
  state,
  rel,
  subjectIds,
  referenceId,
  itemIds,
  allowInverse,
  allowInverseIfShowInverseIsFalse
) => {
  const ref = definitionSelectors.getReferencesById(state).get(rel.reference);
  const testInverse =
    ref.bidirectional || (allowInverse && ref.showInverse) || (allowInverse && allowInverseIfShowInverseIsFalse);

  let match = true;
  let matchInverse = testInverse;

  if (testInverse) {
    if (
      (subjectIds.length && subjectIds.indexOf(rel.object) === -1) ||
      (referenceId && rel.reference !== referenceId) ||
      (itemIds.length && itemIds.indexOf(rel.subject) === -1)
    ) {
      matchInverse = false;
    }
  }

  if (
    (subjectIds.length && subjectIds.indexOf(rel.subject) === -1) ||
    (referenceId && rel.reference !== referenceId) ||
    (itemIds.length && itemIds.indexOf(rel.object) === -1)
  ) {
    match = false;
  }
  return match || matchInverse;
};

const getRelationshipIdsForValues = (
  state,
  subjectIds,
  referenceId,
  itemIds,
  allowInverse,
  allowInverseIfShowInverseIsFalse
) => {
  return state.data.relationships.byId
    .filter(r =>
      relationshipMatches(state, r, subjectIds, referenceId, itemIds, allowInverse, allowInverseIfShowInverseIsFalse)
    )
    .map(r => r.id)
    .toList();
};

const doesRelationshipReferenceItems = (r, itemIds) => {
  return itemIds.indexOf(r.subject) >= 0 || itemIds.indexOf(r.object) >= 0;
};

const getRelationshipsToBeDeletedWithItems = (globalState, itemIds) => {
  return globalState.data.relationships.byId
    .filter(r => doesRelationshipReferenceItems(r, itemIds))
    .map(r => r.id)
    .toList();
};

const getRelationshipsById = state => state.data.relationships.byId;

const getSubjectTypeAllowsRelationshipsForItemIds = (
  itemsById,
  allowedReferencesPerSubjectObjectTypes,
  subjectTypeId,
  itemIds
) => {
  for (let i = 0; i < itemIds.length; i++) {
    const itemId = itemIds[i];
    const itemType = itemsById.getIn([itemId, "type"]);
    const allowedRelationshipsSubject = allowedReferencesPerSubjectObjectTypes.getIn([subjectTypeId, itemType]);
    const allowedRelationshipsObject = allowedReferencesPerSubjectObjectTypes.getIn([itemType, subjectTypeId]);
    if (!allowedRelationshipsSubject.size && !allowedRelationshipsObject.size) {
      return false;
    }
  }
  return true;
};

const areAnyItemsRelatedToItems = (relationshipsById, itemIdSet, targetIdSet) => {
  return Boolean(
    relationshipsById.find(
      r =>
        (itemIdSet.contains(r.subject) && targetIdSet.contains(r.object)) ||
        (itemIdSet.contains(r.object) && targetIdSet.contains(r.subject))
    )
  );
};

const areAnyItemsNotRelatedToItems = (relationshipsById, itemIdSet, targetIdSet) => {
  return Boolean(
    itemIdSet.find(
      itemId =>
        !relationshipsById.find(
          r =>
            (itemId === r.subject && targetIdSet.contains(r.object)) ||
            (itemId === r.object && targetIdSet.contains(r.subject))
        )
    )
  );
};

export default {
  getSubjectRelationshipsWithItems,
  getSubjectInverseRelationshipsWithItems,
  getRelationshipsBetweenItemsWithReferences,
  getRelationshipsBetweenItems,
  getRelationshipIdsForValues,
  getAllowedReferenceBetweenTypes,
  getAllowedReferenceBetweenTypesWithState,
  getAllowedReferenceBetweenItems,
  getAllAllowedReferencesBetweenItems,
  getRelationshipsToBeDeletedWithItems,
  getRelationshipsById,
  getSubjectTypeAllowsRelationshipsForItemIds,
  areAnyItemsRelatedToItems,
  areAnyItemsNotRelatedToItems
};
